import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ListOrdered, CalendarClock } from "lucide-react";
import api from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d || 1).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function Panel({ icon: Icon, title, sub, to, children, testid }) {
  return (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-brand-ink shrink-0" />
          <h3 className="text-brand-ink font-medium truncate">{title}</h3>
          {sub && <span className="text-xs text-brand-tertiary truncate">{sub}</span>}
        </div>
        {to && <Link to={to} className="text-xs text-brand-primary inline-flex items-center gap-1 shrink-0" data-testid={`${testid}-open`}>Open <ArrowRight className="h-3 w-3" /></Link>}
      </div>
      {children}
    </div>
  );
}

const Empty = ({ children }) => <p className="text-sm text-brand-tertiary py-2">{children}</p>;

export function LocationWaitlist({ location }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/waitlist", { params: { location } })
      .then(({ data }) => setRows((data.waitlist || []).filter((w) => (w.status || "active") === "active")))
      .catch(() => setRows([]));
  }, [location]);
  const q = `?location=${encodeURIComponent(location)}`;
  return (
    <Panel icon={ListOrdered} title="Waitlist" sub={rows ? `${rows.length} active` : ""} to={`/sales${q}`} testid="loc-waitlist">
      {!rows ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No one on the waitlist.</Empty> : (
        <div className="divide-y divide-brand-border">
          {rows.slice(0, 8).map((w) => (
            <div key={w.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`loc-waitlist-${w.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-brand-ink truncate">{w.name}</p>
                <p className="text-[11px] text-brand-tertiary truncate">
                  {w.kind === "member" ? `Member ${w.direction || "move"}${w.current_space ? ` from ${w.current_space}` : ""}` : "Tour"}
                  {w.desired_space ? ` → wants ${w.desired_space}` : ""}
                </p>
              </div>
              <span className="text-[11px] text-brand-muted shrink-0">{w.target_date ? `by ${fmtDate(w.target_date)}` : fmtDate(w.date_expressed)}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function LocationMoveOuts({ location }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const month = new Date().toISOString().slice(0, 7);
    api.get("/sales/moveouts/month", { params: { location, month } })
      .then(({ data }) => setRows(data.move_outs || [])).catch(() => setRows([]));
  }, [location]);
  return (
    <Panel icon={CalendarClock} title="Move-outs this month" sub={rows ? `${rows.length}` : ""} to={`/sales?location=${encodeURIComponent(location)}`} testid="loc-moveouts">
      {!rows ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No move-outs logged this month.</Empty> : (
        <div className="divide-y divide-brand-border">
          {rows.slice(0, 8).map((m, i) => (
            <div key={m.id || i} className="py-2.5 flex items-center justify-between gap-3">
              <p className="text-sm text-brand-ink truncate">{m.company}{m.office_number ? <span className="text-brand-tertiary"> · Office {m.office_number}</span> : null}</p>
              <span className="text-[11px] text-brand-muted shrink-0">{fmtDate(m.vacate_date)}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
